import { createFileRoute } from "@tanstack/react-router";
import { Header } from "@/components/Header";
import { BottomNav } from "@/components/BottomNav";
import { Star, Quote, TrendingUp, Wallet, Globe } from "lucide-react";

export const Route = createFileRoute("/testimonials")({
  head: () => ({
    meta: [
      { title: "Testimonials — PataTasks" },
      {
        name: "description",
        content:
          "Real testimonials from testers who earned money testing apps and websites on PataTasks.",
      },
    ],
  }),
  component: TestimonialsPage,
});

const TESTIMONIALS = [
  {
    name: "James Wilson",
    location: "London, United Kingdom",
    gender: "male",
    avatar: "JW",
    earnings: "$42.50",
    tests: 18,
    text: "I was skeptical at first, but after completing about 18 app tests I earned $42.50. The $2 maintenance fee felt annoying upfront, but once I paid it I kept getting more test invites. Withdrew to my PayPal without issues.",
    rating: 4,
    color: "bg-blue-100 text-blue-700",
  },
  {
    name: "Aisha Patel",
    location: "Toronto, Canada",
    gender: "female",
    avatar: "AP",
    earnings: "$37.00",
    tests: 14,
    text: "I tested music and social apps mostly. Earned $37 in my first two weeks. I complained about the $2 fee at first, but after paying I got access to better-paying web app tests. Worth it honestly.",
    rating: 5,
    color: "bg-emerald-100 text-emerald-700",
  },
  {
    name: "Carlos Mendes",
    location: "São Paulo, Brazil",
    gender: "male",
    avatar: "CM",
    earnings: "$29.75",
    tests: 11,
    text: "The maintenance fee caught me off guard, but after paying $2 I completed a few more tests and earned back way more than that. Withdrew via bank transfer and it arrived fast.",
    rating: 4,
    color: "bg-purple-100 text-purple-700",
  },
  {
    name: "Emma Johansson",
    location: "Stockholm, Sweden",
    gender: "female",
    avatar: "EJ",
    earnings: "$55.20",
    tests: 22,
    text: "Best side income I have found online. I do tests in the evening after work. The $2 fee is a one-time thing and honestly it filters out people who are not serious. I have withdrawn $55+ so far.",
    rating: 5,
    color: "bg-amber-100 text-amber-700",
  },
  {
    name: "David Okafor",
    location: "Lagos, Nigeria",
    gender: "male",
    avatar: "DO",
    earnings: "$31.80",
    tests: 13,
    text: "I did not want to pay the $2 fee at first because there are so many scams out there. But after paying, the tests started coming in faster. I used M-Pesa for withdrawal and it was smooth.",
    rating: 4,
    color: "bg-rose-100 text-rose-700",
  },
  {
    name: "Sophie Nguyen",
    location: "Berlin, Germany",
    gender: "female",
    avatar: "SN",
    earnings: "$48.90",
    tests: 19,
    text: "Tested productivity and coding tools mostly. Earned nearly $49. I thought the maintenance fee was annoying, but compared to other platforms this one actually pays out. No hidden stuff.",
    rating: 5,
    color: "bg-teal-100 text-teal-700",
  },
];

function TestimonialsPage() {
  return (
    <div className="bg-background pb-20 md:pb-0">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-600 text-white shadow-lg">
            <Quote className="h-7 w-7" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
            Tester Testimonials
          </h1>
          <p className="mt-2 text-sm text-slate-600 max-w-2xl mx-auto">
            Real feedback from testers around the world who have earned money testing apps and
            websites on PataTasks.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {TESTIMONIALS.map((item, idx) => (
            <div
              key={idx}
              className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm flex flex-col"
            >
              <div className="flex items-center gap-3 mb-3">
                <div
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold ${item.color}`}
                >
                  {item.avatar}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground truncate">{item.name}</p>
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <Globe className="h-3 w-3" />
                    {item.location}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-1 mb-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    className={`h-3.5 w-3.5 ${
                      i < item.rating ? "text-amber-400 fill-amber-400" : "text-slate-300"
                    }`}
                  />
                ))}
              </div>

              <p className="text-xs text-slate-700 leading-relaxed mb-3 flex-1">
                &ldquo;{item.text}&rdquo;
              </p>

              <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                <div className="flex items-center gap-1 text-xs text-slate-600">
                  <TrendingUp className="h-3.5 w-3.5 text-emerald-600" />
                  <span className="font-semibold">{item.earnings}</span>
                  <span className="text-slate-400">earned</span>
                </div>
                <div className="flex items-center gap-1 text-xs text-slate-500">
                  <Wallet className="h-3.5 w-3.5 text-blue-600" />
                  <span>{item.tests} tests</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 text-center">
          <p className="text-xs text-slate-500">
            Testimonials reflect individual experiences. Earnings vary by test availability and
            qualification.
          </p>
        </div>
      </div>
      <BottomNav />
    </div>
  );
}
